/**
 * SceneConfigConverter — coordinate-space conversion between Unity (stored) and
 * the editor's internal right-handed (Y-up) space. This is the EDITOR's internal
 * coordinate model (a legacy of its three.js origins + the +2 Z scene offset);
 * the boilerplate viewer converts Unity → Babylon directly (CreatorElement /
 * PanelResolver) and does not use this, so it lives in the editor, not web-core.
 *
 * Internal space is right-handed with Y-up — named the `'threejs'` CoordinateSpace
 * (a convention label, not a dependency). Unity is left-handed with Y-up; the key
 * difference is the Z-axis direction. The `unityToThreejs` / `threejsToUnity`
 * function names are part of the editor's public API (consumed by creator-web/ via
 * @eyejack/editor) — kept stable.
 *
 * When saving (internal → Unity / "export"):
 * - Invert Z-axis positions
 * - Invert all rotation axes (because of inverted Z)
 * - Scene root gets +2 Z offset (appear in front of the user in Unity)
 *
 * When loading (Unity → internal / "import"):
 * - Invert Z-axis positions
 * - Invert all rotation axes
 * - Scene root gets +2 Z offset (compensate for the Unity offset)
 */

// --- External (Unity/storage) types ---

export interface SceneConfigAsset {
  assetID: string;
  assetType: string;
  assetPath: string;
}

export interface SceneConfigChild {
  sceneID: string;
  assetID: string;
  position: number[];
  rotation: number[];
  scale: number[];
}

export interface SceneConfigScene {
  sceneID: string;
  position: number[];
  rotation: number[];
  scale: number[];
  children: SceneConfigChild[];
}

export interface SceneConfig {
  version: number;
  settings: { movable: boolean };
  assets: SceneConfigAsset[];
  scenes: SceneConfigScene[];
}

// --- Internal (right-handed, Y-up) types ---

export interface InternalScene {
  sceneID: string;
  position: number[];
  rotation: number[];
  scale: number[];
}

export interface InternalElement {
  sceneID: string;
  assetID: string;
  parentID: string;
  position: number[];
  rotation: number[];
  scale: number[];
}

export interface InternalSceneData {
  version: number;
  settings: { movable: boolean };
  assets: SceneConfigAsset[];
  scenes: InternalScene[];
  elements: InternalElement[];
}

/**
 * Convert a Unity-space SceneConfig to the internal representation.
 * Called when loading scene data from the database/config.
 */
export function unityToThreejs(config: SceneConfig): InternalSceneData {
  const scenes: InternalScene[] = config.scenes.map(
    ({ sceneID, position, rotation, scale }) => ({
      sceneID,
      position: [position[0], position[1], -position[2] + 2], // Invert Z, add 2 offset
      rotation: [-rotation[0], -rotation[1], -rotation[2]],   // Invert all rotations
      scale: [...scale],
    })
  );

  // Flatten children from all scenes into a single elements array with parentID
  const elements: InternalElement[] = config.scenes.flatMap(
    ({ sceneID: parentID, children }) =>
      children.map(({ sceneID, assetID, position, rotation, scale }) => ({
        sceneID,
        assetID,
        parentID,
        position: [position[0], position[1], -position[2]], // Invert Z
        rotation: [-rotation[0], -rotation[1], -rotation[2]], // Invert all rotations
        scale: [...scale],
      }))
  );

  return {
    version: config.version,
    settings: { ...config.settings },
    assets: [...config.assets],
    scenes,
    elements,
  };
}

/**
 * Convert the internal representation back to a Unity-space SceneConfig.
 * Called when saving scene data to the database/config.
 */
export function threejsToUnity(data: InternalSceneData): SceneConfig {
  const scenes: SceneConfigScene[] = data.scenes.map(
    ({ sceneID, position, rotation, scale }) => {
      // Find children belonging to this scene
      const children: SceneConfigChild[] = data.elements
        .filter((el) => el.parentID === sceneID)
        .map(({ sceneID, assetID, position, rotation, scale }) => ({
          sceneID,
          assetID,
          position: [position[0], position[1], -position[2]],   // Invert Z back
          rotation: [-rotation[0], -rotation[1], -rotation[2]],  // Invert rotations back
          scale: [...scale],
        }));

      return {
        sceneID,
        position: [position[0], position[1], -position[2] + 2], // Invert Z, add 2 offset
        rotation: [-rotation[0], -rotation[1], -rotation[2]],   // Invert rotations back
        scale: [...scale],
        children,
      };
    }
  );

  return {
    version: data.version,
    settings: { ...data.settings },
    assets: [...data.assets],
    scenes,
  };
}
